/**
 * AcceptanceDialog — okno „Akceptacja": osoba decydująca wybiera DROGĘ, opcjonalnie dopisuje
 * komentarz i wykonuje decyzję na etapie sprawy (model WF: decyzja = wybór drogi narysowanej
 * w definicji procesu).
 *
 * Otwiera się ZAWSZE z przycisków `DecisionButtons`: z „Akceptacji" (`initialRouteKey = null` →
 * preselekcja drogi domyślnej) albo z przycisku drogi (`initialRouteKey` = ta droga). „Anuluj"
 * i zamknięcie okna tylko zamykają — nic nie wysyłają. Decyzję wysyła przycisk „Wykonaj"
 * (`submitDecision`, ta sama trasa co dawniej panel — bez nowego endpointu).
 *
 * Okno nie trzyma stanu między otwarciami: treść montuje się od zera przy każdym `open`
 * (`AcceptanceDialogBody`), więc komentarz i wybór drogi nigdy nie dziedziczą po poprzednim razie.
 *
 * Drogi dostępne na etapie liczy SERWER (`InstanceStep.availableRoutes`, z warunkami dróg
 * warunkowych) z danych rekordu zamrożonych przy starcie sprawy — dla otwartego etapu zestaw dróg
 * jest więc stały, a okno nie odtwarza ich ze snapshotu. Każda droga pokazuje cel („dalej: <etap>" /
 * „koniec: <etap>") i znacznik „wymaga komentarza". Droga domyślna (`findDefaultRoute`) jest
 * preselekcjonowana; gdy etap ma ≥ 2 drogi i żadna nie jest domyślna, a nic nie wybrano — okno
 * mówi o tym wprost i „Wykonaj" czeka na wybór. Gdy wybrana droga ma „wymagaj komentarza",
 * komentarz jest obowiązkowy (serwer też to sprawdza); długość komentarza ogranicza
 * `PROCESS_DECISION_COMMENT_MAX_LENGTH` (ten sam limit co walidator serwera) — twardo (nie da się
 * wpisać więcej), z licznikiem „N / limit" pod polem.
 *
 * Komentarz renderowany jako czysty tekst (React escape'uje) — nigdy jako HTML.
 *
 * F.3 — REQUIRED_FIELDS_MISSING: gdy proces ma skonfigurowane pola wymagane na wybranej drodze
 * i któreś jest puste na rekordzie, backend odrzuca decyzję z listą brakujących pól
 * (`missing: [{slug,label}]`). Pokazujemy to jako alert w oknie PO próbie wykonania — user musi
 * widzieć KTÓRE pola uzupełnić na karcie rekordu; okno zostaje otwarte.
 *
 * F.4 (ADR-0073) — pole „akceptant na następnym etapie" (jak w starym WF): reguły liczy
 * `resolveAddresseeFields` z celu wybranej drogi i z `defaultRouteVoteClosesStage` kroku. Pole
 * pojawia się tylko, gdy decyzja przesuwa sprawę dalej (głos zamyka etap), i tylko dla etapu
 * docelowego z pulą wieloosobową: wymagane, gdy etap ma „wymagaj wskazania"; opcjonalne (puste =
 * cała pula) w trybie „dowolny" bez flagi; w trybie „wszyscy" / „większość" bez flagi, przy celu
 * „koniec" / scaleniu i przy typach jednoosobowych — brak pola. Pole wymagane jest oznaczone
 * (gwiazdka + `aria-required`), a gdy „Wykonaj" czeka na brakującego adresata, krótki powód stoi
 * obok przycisku (`aria-describedby`). Osoba domyślna etapu docelowego jest wstępnie wybrana,
 * o ile należy do puli (brak lub nieprawidłowa osoba domyślna zostawia pole puste). Wysyłamy
 * wyłącznie wybrane osoby. Gdyby mimo to
 * zabrakło adresata, backend odpowie 422 `ADDRESSEE_REQUIRED` / `ADDRESSEE_NOT_IN_POOL` /
 * `ADDRESSEE_NOT_ALLOWED`, obsłużonym tutaj jako toast (fail-safe).
 *
 * F.5 (ADR-0074) — procedury kontrolne przy decyzji: silnik waliduje decyzję przez tenant-owned
 * hook i zwraca `CONTROL_ERROR` (400, blokada twarda) / `CONTROL_WARNING` (409, miękkie
 * ostrzeżenie — ponowny submit z `warningsAccepted:true` przepuszcza) / `CONTROL_HOOK_FAILED`
 * (500, awaria hooka, fail-closed). Obsłużone reużywalnym `useControlWarning` — pierwsza gałąź
 * `onError` mutacji decyzji, PRZED gałęziami specyficznymi dla procesów.
 *
 * Rekord sprawy usunięty w międzyczasie (`TARGET_RECORD_DELETED`, 404 — sprawa „nie istnieje" do
 * przywrócenia kartoteki, nic nie zapisano): toast „Nie znaleziono kartoteki.", okno się zamyka,
 * a dane procesów są odświeżane — tak samo jak przy etapie rozstrzygniętym przez kogoś innego.
 * Tak samo przy module kartoteki sprawy wyłączonym w międzyczasie (`TARGET_MODULE_INACTIVE`,
 * 402): toast „Moduł tej kartoteki jest nieaktywny.", zamknięcie okna, odświeżenie danych.
 *
 * Pozostałe odmowy silnika, po których sprawa zmieniła stan od otwarcia okna (`ALREADY_DECIDED` —
 * głos już oddany, `INSTANCE_NOT_ACTIVE` — sprawa nie w toku, `NOT_ASSIGNED` — osoba wypadła
 * z akceptantów etapu, `STEP_NOT_FOUND` — etapu nie ma), traktujemy tak samo jak etap rozstrzygnięty
 * przez kogoś innego: toast z czytelnym powodem, zamknięcie okna, odświeżenie danych (lista kodów:
 * `isDecisionCaseChangedCode`). `PERMISSION_DENIED` (403 — brak prawa „Podejmowanie decyzji"
 * w firmie rekordu) dostaje własny toast, ale okno ZOSTAJE otwarte: stan sprawy się nie zmienił.
 * Kod odmowy czytamy z pola `error` odpowiedzi (serwer w `PERMISSION_DENIED` dokłada obok pole
 * `code` z nazwą prawa — jest ono informacją dla programisty, okno go nie czyta).
 *
 * Etap bez żadnej dostępnej drogi (same drogi warunkowe, a dane rekordu z chwili wysłania nie
 * spełniają warunku żadnej — ADR-0111 pkt 6): decyzji drogą nie da się wykonać, bo serwer i tak
 * by ją odrzucił (`NO_AVAILABLE_ROUTE`, nic nie zapisane, sprawa zostaje w toku). Okno pokazuje
 * wtedy alert „Brak dostępnej drogi" z wyjściem (anulowanie przez osobę z uprawnieniem, poprawa
 * kartoteki, ponowne wysłanie), a „Wykonaj" jest nieaktywne i powiązane z alertem przez
 * `aria-describedby`. Cofnięcie do poprzedniego etapu zostaje (jeśli etap na nie pozwala) — po
 * jego wybraniu alert znika, a „Wykonaj" działa jak zwykle. Gdyby serwer mimo to odmówił tym
 * kodem, okno pokazuje toast z komunikatem i zostaje otwarte.
 *
 * Sprawa, która po decyzji nie miała dokąd pójść i przeszła w błąd (`instanceStatus: 'error'` —
 * dziś tylko przy wadliwej definicji: scalenie bez wyjścia, rozdzielenie bez gałęzi albo scalenie
 * czekające na gałąź bez otwartego etapu), dostaje
 * toast ostrzegawczy zamiast zwykłego „Decyzja zapisana" (decyzja jest zapisana, ale user musi
 * wiedzieć, że sprawa wymaga uwagi administratora).
 *
 * „Cofnij do poprzedniego etapu" (`step_back`): gdy serwer zwrócił dla kroku `stepBack` (autor
 * włączył cofanie na etapie, jest dokąd wracać i pytający może teraz decydować), pod listą dróg
 * stoi OSOBNA opcja — nie droga: nie ma celu na schemacie ani przycisku w pasku decyzji. Pokazuje,
 * dokąd wróci sprawa („wraca do: <etap> (<osoby>)"). Wybrana: komentarz jest obowiązkowy, znikają
 * pole adresata i alert pól wymaganych (należą do dróg), a „Wykonaj" wysyła
 * `{ decisionType: 'step_back', comment }` — bez drogi, adresata i potwierdzania ostrzeżeń.
 * Opcja i drogi to jeden wzajemnie wykluczający się wybór (wspólna nazwa grupy radio). Gdy serwer
 * oznaczył cofnięcie jako niemożliwe (`blockedReason`: wszystkie osoby z poprzedniego etapu są
 * niedostępne), opcja jest nieaktywna, z powodem widocznym pod nazwą. Odmowy cofnięcia
 * (`STEP_BACK_*`) zostawiają okno otwarte i odświeżają dane — opcja może się wtedy zmienić.
 * Gdy opcja przestaje być dostępna (znika albo robi się nieaktywna) w chwili, gdy była wybrana,
 * wybór cofnięcia wygasa, ale okno NIE wraca po cichu do drogi domyślnej — komentarz napisany
 * z myślą o cofnięciu nie może zatwierdzić sprawy: żadna droga nie jest wtedy zaznaczona, krótki
 * komunikat (póki opcja jest niedostępna) mówi, że cofnięcie nie jest już możliwe, a „Wykonaj"
 * czeka na świadomy wybór drogi (także przy etapie z jedną drogą). Komentarz zostaje; powrót
 * opcji nie wybiera jej ponownie — ostrzeżenie ustępuje wtedy neutralnej podpowiedzi „wybierz
 * drogę albo cofnięcie" (przy etapie bez dróg: „wybierz cofnięcie"), a oba komunikaty są
 * powiązane z „Wykonaj" przez `aria-describedby`.
 * Okno można otworzyć od razu z wybranym cofnięciem (`initialStepBack`, pozycja w menu „…" paska
 * decyzji): dostępne cofnięcie startuje zaznaczone, a niedostępne w chwili otwarcia daje ten sam
 * stan co wycofanie opisane wyżej (wybór drogi czeka na usera). Komunikat zależy od startu:
 * cofnięcie zablokowane w chwili otwarcia — „Cofnięcie jest niedostępne"; brak cofnięcia w chwili
 * otwarcia albo jego zniknięcie później — „nie jest już możliwe".
 * Po udanym cofnięciu toast mówi, do jakiego etapu wróciła sprawa — nazwa z chwili kliknięcia
 * „Wykonaj" (`stepBackTargetLabel` w zmiennych mutacji), nie z propsów w chwili odpowiedzi.
 */

import type { ReactElement } from 'react';
import { useEffect, useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  type AvailableRoute,
  findDefaultRoute,
  type NoApproversStage,
  PROCESS_DECISION_COMMENT_MAX_LENGTH,
  requiresRouteChoice,
} from '@saas/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';

import { Alert } from '@/components/Alert';
import { Button } from '@/components/Button';
import { InlineMessage } from '@/components/InlineMessage';
import { Modal } from '@/components/Modal';
import { RadioGroup, type RadioOption } from '@/components/RadioGroup';
import { Textarea } from '@/components/Textarea';
import { useToast } from '@/contexts/ToastContext';
import { useControlWarning } from '@/hooks/useControlWarning';
import { resolveAddresseeFields } from '@/lib/processes/addressee-resolution';
import {
  decisionErrorI18nKey,
  isDecisionCaseChangedCode,
  noApproversMessage,
  stepBackNoApproversMessage,
} from '@/lib/processes/approvals-api';
import {
  type InstanceDetail,
  type StepBackInfo,
  submitDecision,
  type SubmitDecisionResult,
} from '@/lib/processes/instance-api';
import { routeDisplayLabel } from '@/lib/processes/route-display';

import { AddresseePicker } from './AddresseePicker';

/**
 * Wartość opcji „Cofnij do poprzedniego etapu" w grupie wyboru. Klucze dróg to id krawędzi
 * (UUID), więc wartość nigdy nie koliduje z żadną drogą.
 */
const STEP_BACK_OPTION_VALUE = 'step_back';

/**
 * Odmowy cofnięcia, po których opcja w oknie może być nieaktualna (etap bez cofania, bez poprzedniego
 * etapu albo bez dostępnych osób): okno zostaje otwarte, ale dane trzeba odświeżyć — nowy stan
 * kroku zmieni albo ukryje opcję.
 */
const STEP_BACK_STALE_OPTION_CODES: ReadonlySet<string> = new Set([
  'STEP_BACK_NOT_ALLOWED',
  'STEP_BACK_NO_PREVIOUS',
  'STEP_BACK_NO_APPROVERS',
]);

/** Jedna brakująca pozycja w błędzie `REQUIRED_FIELDS_MISSING` (backend F.3). */
interface RequiredFieldMissing {
  slug: string;
  label: string;
}

/**
 * Vars mutacji decyzji — payload realnie przekazywany do `mutation.mutate` (NIE pełne body
 * `submitDecision`: `comment`/`nextAddressees` dociągane z domknięcia stanu formularza, patrz
 * `mutationFn` niżej). F.5 (ADR-0074) — `warningsAccepted` domieszany przez `useControlWarning`
 * po potwierdzeniu miękkiego ostrzeżenia procedury kontrolnej (`CONTROL_WARNING`, 409); ponowny
 * submit z tą flagą przepuszcza ostrzeżenie (silnik re-waliduje).
 */
type DecisionMutationVars =
  | {
      /** „Cofnij do poprzedniego etapu" zamiast drogi — `routeKey` i adresaci nie biorą udziału. */
      stepBack: true;
      /**
       * Nazwa etapu docelowego cofnięcia w chwili kliknięcia „Wykonaj" — toast sukcesu bierze ją
       * stąd, a nie z propsów w momencie odpowiedzi (odświeżenie danych w trakcie żądania mogło je
       * zmienić). Wymagana przy cofnięciu, żeby wysyłka bez niej nie przeszła typecheck.
       */
      stepBackTargetLabel: string | null;
    }
  | {
      stepBack?: false;
      /**
       * Wybrana droga — wymagana: decyzji bez drogi okno nie wysyła (etap bez żadnej dostępnej
       * drogi → alert i nieaktywne „Wykonaj", ADR-0111 pkt 6).
       */
      routeKey: string;
      warningsAccepted?: boolean;
    };

export interface AcceptanceDialogProps {
  open: boolean;
  /** Zamknięcie bez wysyłki („Anuluj", Esc, tło) albo po rozstrzygnięciu etapu przez kogoś innego. */
  onClose: () => void;
  /** Droga wybrana przyciskiem; `null` = z przycisku „Akceptacja" (preselekcja drogi domyślnej). */
  initialRouteKey: string | null;
  /** Krok czekający na decyzję osoby, która otworzyła okno. */
  stepInstanceId: string;
  /** Sprawa (instancja procesu) — potrzebna do puli akceptantów przy wskazywaniu adresata. */
  instanceId: string;
  /** Drogi dostępne na etapie (`InstanceStep.availableRoutes`). */
  routes: readonly AvailableRoute[];
  /** Zamrożony snapshot sprawy — z niego wynika, czy cel drogi wymaga wskazania adresata. */
  snapshot: InstanceDetail['snapshot'];
  /**
   * `InstanceStep.defaultRouteVoteClosesStage` kroku — czy głos za drogą domyślną zamknie etap
   * (liczy serwer). Bez tego okno nie wie, czy decyzja przesunie sprawę dalej, a pole „akceptant
   * na następnym etapie" pokazuje się tylko wtedy.
   */
  defaultRouteVoteClosesStage: boolean;
  /**
   * `InstanceStep.stepBack` kroku — opcja „Cofnij do poprzedniego etapu" (cel, osoby, ewentualny
   * powód niedostępności). Brak albo `null` = etap nie pozwala cofnąć sprawy.
   */
  stepBack?: StepBackInfo | null;
  /**
   * Okno otwarte z pozycji „Cofnij do poprzedniego etapu" w menu „…" paska decyzji. Gdy cofnięcie
   * jest dostępne w chwili otwarcia — startuje jako wybrane (komentarz obowiązkowy jak zawsze).
   * Gdy go już nie ma albo jest zablokowane — okno startuje w stanie wycofanego cofnięcia (żadna
   * droga niezaznaczona, „Wykonaj" czeka na świadomy wybór; komunikat „nie jest już możliwe", a przy
   * zablokowanym w chwili otwarcia — „jest niedostępne").
   * Domyślnie `false`; wartość czytana tylko przy otwarciu (okno nie trzyma stanu między otwarciami).
   */
  initialStepBack?: boolean;
  /**
   * Wywołane po zapisaniu decyzji (po toaście i unieważnieniu danych procesów). Wołający MUSI
   * wtedy zamknąć okno: po udanym cofnięciu stan okna (wybrane cofnięcie, które zostało już
   * wykonane) nie odpowiada odświeżonym danym kroku.
   */
  onDecided: (result: SubmitDecisionResult) => void;
  testIdPrefix?: string;
}

export function AcceptanceDialog({ open, ...rest }: AcceptanceDialogProps): ReactElement | null {
  return open ? <AcceptanceDialogBody {...rest} /> : null;
}

function AcceptanceDialogBody({
  onClose,
  initialRouteKey,
  stepInstanceId,
  instanceId,
  routes,
  snapshot,
  defaultRouteVoteClosesStage,
  stepBack = null,
  initialStepBack = false,
  onDecided,
  testIdPrefix = 'acceptance',
}: Omit<AcceptanceDialogProps, 'open'>): ReactElement {
  const { t } = useTranslation();
  const { show } = useToast();
  const queryClient = useQueryClient();

  const [comment, setComment] = useState('');
  // Droga wybrana RĘCZNIE albo przyciskiem ('' = nic nie wybrano → obowiązuje preselekcja domyślnej).
  const [chosenRouteKey, setChosenRouteKey] = useState(initialRouteKey ?? '');
  // Powód zablokowanego „Wykonaj" (brak adresata) — powiązany z przyciskiem przez aria-describedby.
  const addresseeReasonId = useId();
  // Komunikaty po wycofaniu cofnięcia (ostrzeżenie / podpowiedź o ponownym wyborze) — tak samo
  // powiązane z „Wykonaj", żeby czytnik ekranu podał powód zablokowanego przycisku.
  const withdrawnNoticeId = useId();
  const withdrawnHintId = useId();
  // Komunikat „etap nie ma drogi domyślnej — wybierz drogę" (też powód zablokowanego „Wykonaj").
  const noDefaultRouteId = useId();
  // Alert „Brak dostępnej drogi" (etap bez żadnej drogi) — też powód zablokowanego „Wykonaj".
  const noAvailableRouteId = useId();
  // Brakujące pola z odpowiedzi serwera + droga, której dotyczą (każda droga ma własną listę).
  // Alert pokazujemy tylko dla drogi, która nadal obowiązuje.
  const [requiredFieldsMissing, setRequiredFieldsMissing] = useState<{
    routeKey: string;
    missing: RequiredFieldMissing[];
  } | null>(null);
  // F.4 — wybrana osoba per etap docelowy z polem „akceptant na następnym etapie" ('' = brak).
  const [addressees, setAddressees] = useState<Record<string, string>>({});
  // „Cofnij do poprzedniego etapu" wybrane zamiast drogi (wzajemnie wykluczające się z drogami).
  // Otwarte z pozycji „Cofnij do poprzedniego etapu" w menu „…" (`initialStepBack`): dostępne
  // cofnięcie startuje jako wybrane.
  const [stepBackChosen, setStepBackChosen] = useState(
    initialStepBack && stepBack !== null && stepBack.blockedReason === null,
  );
  // Cofnięcie było wybrane, ale opcja przestała być dostępna (odświeżenie danych: odmowa `STEP_BACK_*`,
  // zmiana w tle). Wtedy żadna droga nie jest preselekcjonowana — „Wykonaj" czeka na wybór drogi
  // przez usera, żeby komentarz napisany do cofnięcia nie zatwierdził sprawy drogą domyślną.
  // Start z `initialStepBack`, gdy cofnięcia w chwili otwarcia już nie ma (albo jest zablokowane) —
  // to samo wycofanie co po zmianie w tle: żadna droga się nie zaznacza, „Wykonaj" czeka na wybór.
  const [stepBackWithdrawn, setStepBackWithdrawn] = useState(
    initialStepBack && (stepBack === null || stepBack.blockedReason !== null),
  );
  // Okno otwarte z cofnięcia, które w chwili otwarcia było ZABLOKOWANE (jest, ale nikt z poprzedniego
  // etapu nie jest dostępny) — komunikat mówi wtedy „niedostępne", a nie „nie jest już możliwe"
  // (to drugie dotyczy cofnięcia, które zniknęło albo którego nie było).
  const [startedBlocked] = useState(
    initialStepBack && stepBack !== null && stepBack.blockedReason !== null,
  );
  // Cofnięcie wysłane i jeszcze nieodrzucone (żądanie trwa albo się powiodło). W tym czasie
  // odświeżenie danych zwykle zdejmuje opcję (sprawa właśnie wraca) — to nie jest wycofanie
  // wyboru, więc okno nie pokazuje „Cofnięcie nie jest już możliwe" tuż przed toastem sukcesu.
  // Odmowa serwera zdejmuje flagę (`onError`); wtedy niedostępna opcja normalnie wygasa.
  const [stepBackSubmitting, setStepBackSubmitting] = useState(false);

  const choiceRequired = requiresRouteChoice(routes);
  const defaultRoute = findDefaultRoute(routes);
  const hasRoutes = routes.length > 0;
  const stepBackAvailable = stepBack !== null && stepBack.blockedReason === null;
  if (stepBackChosen && !stepBackAvailable && !stepBackSubmitting) {
    // Dopasowanie stanu do propsów w trakcie renderu (wzorzec z dokumentacji Reacta) — bez
    // pośredniego renderu z drogą domyślną. Gdy opcja wróci, sama się już nie zaznaczy.
    setStepBackChosen(false);
    setStepBackWithdrawn(true);
  }
  const stepBackActive = stepBackChosen && (stepBackAvailable || stepBackSubmitting);
  // Obowiązująca droga: wybór usera (kliknięty przycisk albo pozycja listy) znaleziony wśród dróg
  // etapu, a gdy go nie było — domyślna / jedyna (preselekcja). Przy cofnięciu i po jego
  // wycofaniu (do świadomego wyboru drogi) żadna droga.
  const selectedRoute =
    stepBackActive || stepBackWithdrawn
      ? null
      : ((chosenRouteKey ? routes.find((r) => r.routeKey === chosenRouteKey) : undefined) ??
        defaultRoute ??
        null);
  const effectiveRouteKey = selectedRoute?.routeKey ?? '';
  // Cofnięcie zawsze wymaga komentarza (po co sprawa wraca); droga — tylko gdy tak ustawił autor.
  const commentRequired = stepBackActive || selectedRoute?.requireComment === true;

  // Alert „brakujące pola" dotyczy drogi, na której decyzja została odrzucona (każda droga ma
  // własną listę pól). Każda zmiana OBOWIĄZUJĄCEJ drogi go unieważnia — zwykle robi to już
  // `onChange` listy wyboru; efekt pilnuje tego niezależnie od źródła zmiany.
  useEffect(() => {
    setRequiredFieldsMissing(null);
  }, [effectiveRouteKey]);

  // F.4 — etapy docelowe wybranej drogi, przy których pokazujemy pole „akceptant na następnym
  // etapie" (z informacją, czy jest wymagane i kogo podpowiedzieć).
  const addresseeFields = useMemo(
    () =>
      resolveAddresseeFields({
        nodes: snapshot.nodes,
        edges: snapshot.edges,
        routes,
        selectedRoute,
        defaultRouteVoteClosesStage,
      }),
    [snapshot, routes, selectedRoute, defaultRouteVoteClosesStage],
  );

  // F.5 (ADR-0074) — `mutate` celowo owinięty w lambdę: `mutation` deklarowana niżej, ale to
  // wywołanie trafia dopiero do zamknięcia `handleConfirm` hooka, uruchamianego po pełnym
  // mount'cie (patrz analogiczny wzorzec w PurchaseInvoiceDetail.tsx).
  const controlWarning = useControlWarning<DecisionMutationVars>({
    mutate: (payload) => mutation.mutate(payload),
    // Okno decyzji procesu (nie zapis dokumentu) — własne teksty zamiast domyślnych
    // „…kontroli dokumentu" / „Zapisz mimo to".
    titleKey: 'tenant.processes.decision.controlWarning.title',
    confirmLabelKey: 'tenant.processes.decision.controlWarning.confirmLabel',
    // Blokada bez treści od procedury: tekst o decyzji, nie o zapisie dokumentu.
    errorKey: 'tenant.processes.actions.controlError',
    // Ostrzeżenie bez treści od procedury: zdanie zamiast powtórzenia tytułu-pytania.
    warningFallbackKey: 'tenant.processes.decision.controlWarning.fallbackMessage',
  });

  const mutation = useMutation({
    mutationFn: (vars: DecisionMutationVars) => {
      // Cofnięcie to osobna akcja etapu: sam komentarz (obowiązkowy), bez drogi, adresata
      // i potwierdzania ostrzeżeń — te należą do dróg.
      if (vars.stepBack) {
        return submitDecision(stepInstanceId, {
          decisionType: 'step_back',
          comment: comment.trim(),
        });
      }
      // Tylko wybrane osoby: puste pole opcjonalne = cała pula etapu (bez wpisu dla serwera),
      // a puste wymagane blokuje „Wykonaj". Wybór dla etapu spoza bieżących pól (inna droga
      // wybrana wcześniej) nie trafia do decyzji.
      const nextAddressees = addresseeFields.flatMap((field) => {
        const userId = addressees[field.node.id];
        return userId ? [{ nodeId: field.node.id, userId }] : [];
      });
      return submitDecision(stepInstanceId, {
        decisionType: 'approve',
        ...(comment.trim() ? { comment: comment.trim() } : {}),
        routeKey: vars.routeKey,
        ...(nextAddressees.length > 0 ? { nextAddressees } : {}),
        ...(vars.warningsAccepted ? { warningsAccepted: true } : {}),
      });
    },
    onSuccess: (result, variables) => {
      if (result.instanceStatus === 'error') {
        // Decyzja jest zapisana, ale sprawa nie miała dokąd pójść i przeszła w błąd — sam toast
        // ostrzegawczy (jego treść zaczyna się od „Decyzja została zapisana…").
        show({ variant: 'warning', title: t('tenant.processes.actions.instanceErrored') });
      } else if (variables.stepBack) {
        // Cofnięcie to nie „decyzja" w rozumieniu dróg — toast mówi, dokąd wróciła sprawa.
        const stage = variables.stepBackTargetLabel?.trim() ?? '';
        show({
          variant: 'success',
          title: stage
            ? t('tenant.processes.acceptance.stepBack.done', { stage })
            : t('tenant.processes.acceptance.stepBack.doneUnnamed'),
        });
      } else {
        show({
          variant: 'success',
          title: t('tenant.processes.actions.saved', 'Decyzja zapisana'),
        });
      }
      void queryClient.invalidateQueries({ queryKey: ['processes'] });
      onDecided(result);
    },
    onError: (
      err: AxiosError<{
        error?: string;
        missing?: RequiredFieldMissing[];
        nodeIds?: string[];
        nodeId?: string;
        userId?: string;
        stages?: NoApproversStage[];
      }>,
      variables: DecisionMutationVars,
    ) => {
      // Cofnięcie odrzucone — nic nie zapisano; od teraz niedostępna opcja wygasa jak zwykle.
      setStepBackSubmitting(false);
      // F.5 (ADR-0074) — procedury kontrolne mają pierwszeństwo: CONTROL_ERROR/CONTROL_WARNING/
      // CONTROL_HOOK_FAILED obsłużone (toast lub dialog już pokazany), reszta gałęzi
      // (specyficzne dla procesów) nie musi się odpalać.
      if (controlWarning.handleError(err, variables)) return;
      const code = err.response?.data?.error;
      if (code === 'REQUIRED_FIELDS_MISSING') {
        // Okno zostaje otwarte — user musi widzieć listę pól do uzupełnienia na karcie rekordu.
        // Zero toastu tutaj (dublowałby alert w oknie).
        setRequiredFieldsMissing({
          routeKey: variables.stepBack ? '' : variables.routeKey,
          missing: err.response?.data?.missing ?? [],
        });
        return;
      }
      setRequiredFieldsMissing(null);
      if (code !== undefined && STEP_BACK_STALE_OPTION_CODES.has(code)) {
        // Cofnięcie odrzucone, nic nie zapisano, a opcja w oknie mogła się zdezaktualizować (osoby
        // z poprzedniego etapu straciły dostęp, etap przestał pozwalać na cofanie). Okno zostaje
        // otwarte — można wybrać drogę — a odświeżone dane pokażą aktualny stan opcji.
        void queryClient.invalidateQueries({ queryKey: ['processes'] });
      }
      if (code === 'STEP_BACK_NO_APPROVERS') {
        // Komunikat z nazwą etapu z odpowiedzi (jak przy `NO_APPROVERS`, ale o niedostępnych osobach).
        show({
          variant: 'error',
          title: stepBackNoApproversMessage(t, err.response?.data?.stages),
        });
        return;
      }
      if (isDecisionCaseChangedCode(code)) {
        // Sprawa zmieniła stan od otwarcia okna: ktoś inny rozstrzygnął ten etap, głos tej osoby
        // jest już oddany, osoba wypadła z akceptantów, sprawa przestała być w toku, etapu nie ma,
        // rekord sprawy został usunięty albo moduł jego kartoteki wyłączono (402 — sprawa jest po
        // stronie serwera ukryta); w każdym z tych przypadków nic nie zapisano — odśwież widok,
        // panel / wiersz sam zniknie. Okno zamykamy od razu: po odświeżeniu może się pojawić NOWY
        // krok i nie chcemy go pokazać z komentarzem wpisanym do poprzedniego. Czytelny komunikat
        // (co się zmieniło) idzie toastem niżej. Brak prawa do decyzji (`PERMISSION_DENIED`) tu nie
        // wchodzi — stan sprawy się nie zmienił, więc okno zostaje otwarte.
        onClose();
        void queryClient.invalidateQueries({ queryKey: ['processes'] });
      }
      if (code === 'NO_APPROVERS') {
        // Kolejny etap bez akceptantów — decyzja NIE została zapisana; nazwy etapów z odpowiedzi
        // mówią, czego dotyczy prośba do administratora. Okno zostaje otwarte (można wybrać inną
        // drogę albo wrócić po uzupełnieniu ustawień).
        show({ variant: 'error', title: noApproversMessage(t, err.response?.data?.stages) });
        return;
      }
      const knownKey = decisionErrorI18nKey(code);
      show({
        variant: 'error',
        title: knownKey
          ? t(knownKey)
          : t('tenant.processes.actions.error', 'Nie udało się zapisać decyzji'),
      });
    },
  });

  const allAddresseesSelected = addresseeFields.every(
    (field) => !field.required || !!addressees[field.node.id],
  );
  // Brak drogi blokuje „Wykonaj" także po wycofaniu cofnięcia — wtedy przy dowolnej liczbie dróg
  // (również jednej), bo preselekcji nie ma. Etap bez żadnej dostępnej drogi blokuje zawsze
  // (serwer odrzuciłby decyzję `NO_AVAILABLE_ROUTE`) — zostaje tylko cofnięcie, jeśli jest.
  const routeMissing =
    (choiceRequired || stepBackWithdrawn || !hasRoutes) &&
    selectedRoute === null &&
    !stepBackActive;
  // Alert „Brak dostępnej drogi" — etap bez dróg, a cofnięcie nie jest wybrane.
  const noAvailableRouteShown = !hasRoutes && !stepBackActive;
  const commentMissing = commentRequired && comment.trim().length === 0;
  // Brakujące pola dotyczą drogi — przy cofnięciu (żadnej drogi) alertu nie ma.
  const missingFieldsForRoute =
    !stepBackActive && requiredFieldsMissing && requiredFieldsMissing.routeKey === effectiveRouteKey
      ? requiredFieldsMissing.missing
      : null;

  const closeModal = (): void => {
    if (mutation.isPending) return;
    onClose();
  };

  const unnamed = t('tenant.processes.actions.routeUnnamed');
  // Komunikat o wycofanym cofnięciu stoi, dopóki opcja jest niedostępna; gdy wróci, wybór drogi
  // nadal czeka na usera (bez preselekcji), ale „nie jest już możliwe" byłoby nieprawdą.
  const withdrawnNoticeShown = stepBackWithdrawn && !stepBackAvailable;
  // Bez dróg nie ma co wybierać — komunikat odsyła wtedy do zamknięcia okna zamiast do wyboru drogi.
  const withdrawnMessageKey = startedBlocked
    ? hasRoutes
      ? 'tenant.processes.acceptance.stepBack.unavailable'
      : 'tenant.processes.acceptance.stepBack.unavailableNoRoutes'
    : hasRoutes
      ? 'tenant.processes.acceptance.stepBack.withdrawn'
      : 'tenant.processes.acceptance.stepBack.withdrawnNoRoutes';
  const withdrawnMessage = t(withdrawnMessageKey);
  // Opcja wróciła, a user jeszcze niczego nie wybrał (żadna grupa nie jest zaznaczona, „Wykonaj"
  // czeka) — neutralna podpowiedź zamiast ostrzeżenia, które przestało być prawdziwe.
  const withdrawnHintShown = stepBackWithdrawn && stepBackAvailable;
  const withdrawnHintMessage = hasRoutes
    ? t('tenant.processes.acceptance.stepBack.withdrawnRestored')
    : t('tenant.processes.acceptance.stepBack.withdrawnRestoredNoRoutes');
  // Komunikaty w oknie, które tłumaczą zablokowane „Wykonaj" (etap bez żadnej drogi, brak drogi
  // przy etapie bez drogi domyślnej, wycofane cofnięcie, brak adresata) — id oddzielone spacją
  // albo brak atrybutu. Wymagany komentarz opisuje podpowiedź przy samym polu komentarza.
  const noDefaultRouteShown = hasRoutes && routeMissing && !stepBackWithdrawn;
  const confirmDescribedBy =
    [
      noAvailableRouteShown ? noAvailableRouteId : null,
      noDefaultRouteShown ? noDefaultRouteId : null,
      withdrawnNoticeShown ? withdrawnNoticeId : null,
      withdrawnHintShown ? withdrawnHintId : null,
      allAddresseesSelected ? null : addresseeReasonId,
    ]
      .filter((id): id is string => id !== null)
      .join(' ') || undefined;

  // Cel drogi: „dalej: <etap>" / „koniec: <etap>"; gdy etap nie ma nazwy — samo „dalej" / „koniec".
  const describeRoute = (route: AvailableRoute): string => {
    const stage = route.targetLabel?.trim() ?? '';
    const target = route.targetIsEnd
      ? stage
        ? t('tenant.processes.acceptance.targetEnd', { stage })
        : t('tenant.processes.acceptance.targetEndUnnamed')
      : stage
        ? t('tenant.processes.acceptance.targetNext', { stage })
        : t('tenant.processes.acceptance.targetNextUnnamed');
    return route.requireComment
      ? `${target} · ${t('tenant.processes.acceptance.requiresComment')}`
      : target;
  };

  const routeOptions: RadioOption[] = routes.map((route) => ({
    value: route.routeKey,
    label: routeDisplayLabel(route, unnamed),
    description: describeRoute(route),
    disabled: mutation.isPending,
  }));

  // Opcja cofnięcia: cel i osoby („wraca do: <etap> (<osoby>)") albo — gdy nikt z poprzedniego
  // etapu nie jest już dostępny — nieaktywna z powodem pod nazwą.
  const describeStepBack = (
    info: StepBackInfo,
  ): { description?: string; disabledReason?: string } => {
    const stage = info.targetStageLabel?.trim() ?? '';
    if (info.blockedReason === 'NO_APPROVERS') {
      return {
        disabledReason: stage
          ? t('tenant.processes.acceptance.stepBack.blockedNoApprovers', { stage })
          : t('tenant.processes.acceptance.stepBack.blockedNoApproversUnnamed'),
      };
    }
    const users = info.targetUsers
      .map(
        (user) => user.displayName?.trim() || t('tenant.processes.acceptance.stepBack.unnamedUser'),
      )
      .join(', ');
    if (!users) return {};
    return {
      description: stage
        ? t('tenant.processes.acceptance.stepBack.target', { stage, users })
        : t('tenant.processes.acceptance.stepBack.targetUnnamed', { users }),
    };
  };
  const stepBackOptions: RadioOption[] = stepBack
    ? [
        {
          value: STEP_BACK_OPTION_VALUE,
          label: t('tenant.processes.acceptance.stepBack.label'),
          ...describeStepBack(stepBack),
          disabled: mutation.isPending || stepBack.blockedReason !== null,
        },
      ]
    : [];

  return (
    <>
      <Modal
        open
        onClose={closeModal}
        size="md"
        title={t('tenant.processes.actions.approve')}
        footer={
          <div className="flex min-w-0 flex-col items-end gap-2">
            {allAddresseesSelected ? null : (
              <InlineMessage
                tone="info"
                id={addresseeReasonId}
                data-testid={`${testIdPrefix}-addressee-missing`}
              >
                {t('tenant.processes.acceptance.addresseeMissing')}
              </InlineMessage>
            )}
            <div className="flex justify-end gap-2">
              <Button
                variant="secondary"
                size="sm"
                data-testid={`${testIdPrefix}-cancel`}
                onClick={closeModal}
                disabled={mutation.isPending}
              >
                {t('common.cancel')}
              </Button>
              <Button
                size="sm"
                data-testid={`${testIdPrefix}-confirm`}
                aria-describedby={confirmDescribedBy}
                disabled={
                  mutation.isPending || routeMissing || commentMissing || !allAddresseesSelected
                }
                onClick={() => {
                  if (mutation.isPending) return;
                  if (stepBackActive) {
                    // Flaga tylko tutaj: cofnięcie nie wraca przez ponowienie po ostrzeżeniu
                    // procedury kontrolnej (silnik kończy cofnięcie przed procedurami dróg, a serwer
                    // przyjmuje potwierdzenie ostrzeżeń wyłącznie przy wyborze drogi).
                    setStepBackSubmitting(true);
                    // Nazwa etapu z chwili kliknięcia: odświeżenie danych w trakcie żądania może
                    // zmienić albo zdjąć `stepBack` z propsów, a toast powstaje dopiero po odpowiedzi.
                    mutation.mutate({
                      stepBack: true,
                      stepBackTargetLabel: stepBack?.targetStageLabel ?? null,
                    });
                    return;
                  }
                  // Decyzja bez drogi nie istnieje (etap bez dróg → alert, „Wykonaj" nieaktywne;
                  // brak wyboru przy ≥ 2 drogach → `routeMissing`) — bez drogi nic nie wysyłamy.
                  if (!selectedRoute) return;
                  mutation.mutate({ routeKey: selectedRoute.routeKey });
                }}
              >
                {t('tenant.processes.acceptance.execute')}
              </Button>
            </div>
          </div>
        }
      >
        {missingFieldsForRoute && (
          <Alert
            tone="error"
            title={t('tenant.processes.actions.requiredFieldsMissing.title')}
            data-testid={`${testIdPrefix}-required-fields-error`}
            className="mb-3"
          >
            <p>{t('tenant.processes.actions.requiredFieldsMissing.message')}</p>
            <ul className="mt-1 list-disc pl-4">
              {missingFieldsForRoute.map((f) => (
                <li key={f.slug}>{f.label}</li>
              ))}
            </ul>
          </Alert>
        )}

        {noAvailableRouteShown ? (
          <Alert
            tone="warning"
            id={noAvailableRouteId}
            title={t('tenant.processes.acceptance.noAvailableRoute.title')}
            className="mb-3"
            data-testid={`${testIdPrefix}-no-available-route`}
          >
            {t('tenant.processes.acceptance.noAvailableRoute.message')}
          </Alert>
        ) : null}

        {withdrawnNoticeShown ? (
          <InlineMessage
            tone="warning"
            id={withdrawnNoticeId}
            className="mb-3"
            data-testid={`${testIdPrefix}-step-back-withdrawn`}
          >
            {withdrawnMessage}
          </InlineMessage>
        ) : null}

        {withdrawnHintShown ? (
          <InlineMessage
            tone="info"
            id={withdrawnHintId}
            className="mb-3"
            data-testid={`${testIdPrefix}-step-back-withdrawn-hint`}
          >
            {withdrawnHintMessage}
          </InlineMessage>
        ) : null}

        {hasRoutes ? (
          <div className="mb-3">
            <RadioGroup
              name={`${testIdPrefix}-route`}
              legend={t('tenant.processes.actions.route')}
              data-testid={`${testIdPrefix}-routes`}
              value={selectedRoute?.routeKey ?? null}
              options={routeOptions}
              onChange={(routeKey) => {
                setChosenRouteKey(routeKey);
                setStepBackChosen(false);
                setStepBackWithdrawn(false);
                // Brakujące pola dotyczyły POPRZEDNIO wybranej drogi (każda ma własną listę) —
                // po zmianie drogi czerwony alert byłby nieprawdziwy.
                setRequiredFieldsMissing(null);
              }}
            />
            {noDefaultRouteShown ? (
              <InlineMessage
                id={noDefaultRouteId}
                tone="warning"
                className="mt-2"
                data-testid={`${testIdPrefix}-no-default-route`}
              >
                {t('tenant.processes.acceptance.noDefaultRoute')}
              </InlineMessage>
            ) : null}
          </div>
        ) : null}

        {stepBack ? (
          // Osobna akcja, nie droga — przy drogach wydzielona kreską i własnym nagłówkiem („Inna
          // akcja" ma sens tylko obok dróg; bez nich opcja stoi sama, bez nagłówka i kreski). Ta
          // sama nazwa grupy radio co drogi: wybór cofnięcia i wybór drogi wykluczają się nawet
          // w przeglądarce.
          <div
            className={
              hasRoutes ? 'mb-3 border-t border-dashed border-border-primary pt-3' : 'mb-3'
            }
          >
            <RadioGroup
              name={`${testIdPrefix}-route`}
              legend={hasRoutes ? t('tenant.processes.acceptance.stepBack.legend') : undefined}
              data-testid={`${testIdPrefix}-step-back`}
              value={stepBackActive ? STEP_BACK_OPTION_VALUE : null}
              options={stepBackOptions}
              onChange={() => {
                setStepBackChosen(true);
                setStepBackWithdrawn(false);
                // Brakujące pola dotyczyły wybranej wcześniej drogi — cofnięcie ich nie ma.
                setRequiredFieldsMissing(null);
              }}
            />
          </div>
        ) : null}

        <Textarea
          data-testid={`${testIdPrefix}-comment`}
          label={t('tenant.processes.actions.comment')}
          hint={
            stepBackActive
              ? t('tenant.processes.acceptance.stepBack.commentRequiredHint')
              : commentRequired
                ? t('tenant.processes.actions.commentRequiredHint')
                : undefined
          }
          required={commentRequired}
          maxLength={PROCESS_DECISION_COMMENT_MAX_LENGTH}
          showCount
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          disabled={mutation.isPending}
          rows={3}
        />

        {addresseeFields.map(({ node, required, defaultUserId }) => (
          <div key={node.id}>
            <AddresseePicker
              instanceId={instanceId}
              nodeId={node.id}
              nodeLabel={node.label ?? node.id}
              defaultAddresseeUserId={defaultUserId ?? undefined}
              value={addressees[node.id] ?? ''}
              onChange={(userId) => setAddressees((prev) => ({ ...prev, [node.id]: userId }))}
              required={required}
              disabled={mutation.isPending}
              testIdPrefix={`${testIdPrefix}-addressee`}
            />
            {required ? null : (
              <p
                className="mt-1 text-xs text-text-tertiary"
                data-testid={`${testIdPrefix}-addressee-${node.id}-optional`}
              >
                {t('tenant.processes.actions.addressee.optionalHint')}
              </p>
            )}
          </div>
        ))}
      </Modal>

      {controlWarning.dialog}
    </>
  );
}
